import { useEffect, useState, lazy, Suspense } from 'react';
import { Link } from "react-router-dom";
import Typewriter from "typewriter-effect";

// Lazy load components for better performance
const GoogleReviewSection = lazy(() => import("./GoogleReviewSection.js"));
const ServiceCard = lazy(() => import("./ServiceCard.js"));
const Section4 = lazy(() => import("./Section4.js"));
const LogoSection = lazy(() => import("./LogoSection.js"));
const HomeFAQs = lazy(() => import("./HomeFAQs.js"));
const WhatWeDoSection = lazy(() => import("./WhatWeDoSection.js"));
const ContactForm = lazy(() => import("../ContactForm/ContactFormModal.js"));

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    const createdMeta = [];

    const setMetaTag = ({ attribute, value, content }) => {
      const selector = `meta[${attribute}="${value}"]`;
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
        createdMeta.push(meta);
      }
      meta.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      return link;
    };

    const createJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      return script;
    };

    document.title = 'Bookkeeping, Tax & Company Formation Services in USA | BooksComply';

    setMetaTag({
      attribute: 'name',
      value: 'description',
      content: 'Streamline bookkeeping, tax filing, and company formation in the USA. Cost-effective, compliant services powered by expert U.S. accountants.'
    });
    setMetaTag({
      attribute: 'name',
      value: 'keywords',
      content: 'US bookkeeping outsourcing, US tax filing partner, virtual CFO USA, USA company incorporation support, BooksComply accountants'
    });
    setMetaTag({ attribute: 'name', value: 'robots', content: 'index, follow' });
    setMetaTag({ attribute: 'property', value: 'og:title', content: 'Bookkeeping, Tax & Company Formation Services in USA | BooksComply' });
    setMetaTag({ attribute: 'property', value: 'og:description', content: 'Streamline bookkeeping, tax filing, and company formation in the USA. Cost-effective, compliant services powered by expert U.S. accountants.' });
    setMetaTag({ attribute: 'property', value: 'og:image', content: 'https://www.bookscomply.com/Assets/Images/Front_Image.png' });
    setMetaTag({ attribute: 'property', value: 'og:url', content: 'https://www.bookscomply.com/' });
    setMetaTag({ attribute: 'name', value: 'twitter:card', content: 'summary_large_image' });

    const canonicalLink = upsertCanonical('https://www.bookscomply.com/');

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BooksComply',
      url: 'https://www.bookscomply.com',
      logo: 'https://www.bookscomply.com/Assets/Images/BooksCompl.png',
      description: 'BooksComply provides end-to-end US accounting, bookkeeping, tax filing, and incorporation solutions for founders worldwide.',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 9428482371',
        contactType: 'customer service',
        areaServed: 'USA'
      },
      sameAs: [
        'https://in.linkedin.com/company/bookscomply',
        'https://x.com/njp_advisors'
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '124'
      }
    };

    const servicesSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'BooksComply USA Expansion Services',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/tax-return',
            name: 'Tax Return Services USA',
            description: 'Forget the tax season headaches. Our team will prepare and file your federal and state returns and guide you on every required form.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Tax Preparation',
            serviceOutput: 'Filed US federal and state tax returns with compliance support',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/tax-return',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/bookkeeping',
            name: 'U.S. Bookkeeping Services',
            description: 'Stay prepared with dependable bookkeeping services USA. Outsource your bookkeeping to our team, and enjoy the good financial reports and reliable compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Bookkeeping Service',
            serviceOutput: 'GAAP-compliant monthly financial statements',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/bookkeeping',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/cfo-advisory',
            name: 'Virtual CFO Services USA for Startups',
            description: 'Grow the same with confidence. With our virtual CFO services for startups USA, you’ll gain niche financial insights and better decision-making—without hiring a CFO.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Financial Advisory Service',
            serviceOutput: 'Strategic finance models and investor-ready dashboards',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/cfo-advisory',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/usa-company-formation',
            name: 'USA Company Formation Services',
            description: 'Build the U.S. business incorporation simple. We guide you through state selection, document preparation, EIN registration, and IRS compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Business Incorporation Service',
            serviceOutput: 'Formed US legal entity with compliance roadmap',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/usa-company-formation',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        }
      ]
    };

    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'Chirag Darji'
      },
      itemReviewed: {
        '@type': 'Organization',
        name: 'BooksComply'
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      reviewBody: 'BooksComply handled our Wyoming incorporation, filings, and ongoing compliance with speed and transparency.'
    };

    const organizationScript = createJsonLd('home-organization-schema', organizationSchema);
    const servicesScript = createJsonLd('home-services-schema', servicesSchema);
    const reviewScript = createJsonLd('home-review-schema', reviewSchema);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How to start a company in the USA?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "BooksComply guides you through every state’s requirements—step-by-step. Stay informed at each stage with regular updates."
          }
        },
        {
          '@type': 'Question',
          name: 'How long does U.S. company formation take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most businesses are set up in just a few days. Non-resident founders may experience a longer wait for EIN registration.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I outsource U.S. tax filing if I live abroad?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes—you can! BooksComply supports expat entrepreneurs worldwide, managing federal and state filings remotely.'
          }
        },
        {
          '@type': 'Question',
          name: 'What are the most affordable bookkeeping services in the USA for startups?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many startups look for bookkeeping that is budget-friendly yet reliable. Services like BooksComply specialize in affordable bookkeeping solutions tailored for small businesses and startups'
          }
        }
      ]
    };
    const faqScript = createJsonLd('home-faq-schema', faqSchema);

    return () => {
      createdMeta.forEach((meta) => document.head.removeChild(meta));
      [organizationScript, servicesScript, reviewScript, faqScript].forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      if (canonicalLink && canonicalLink.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    };
  }, []);

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  return (
    <main className="font-['Inter','Poppins','Segoe_UI',-apple-system,BlinkMacSystemFont,'Roboto','Helvetica_Neue',sans-serif]">
      {/* Hero Section */}
      <section
        id="homeSection"
        className="min-h-[50vh] flex items-center bg-white relative z-[1] py-4 sm:py-6 md:py-8 lg:py-10 xl:py-12"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            {/* Left Column: Taglines */}
            <div className="">
              <h1 className="text-[1.75rem] sm:text-[2rem] md:text-[2.25rem] lg:text-[2.5rem] xl:text-[3rem] font-extrabold mb-3 leading-tight">
                <span className="text-black">Grow Your U.S. Business with Trusted</span>
                <br />
                <span className="text-[#A30000] font-normal text-[1.5rem] sm:text-[1.75rem] md:text-[2rem] lg:text-[2.25rem] xl:text-[2.75rem] leading-tight bg-gradient-to-t from-[#fff3cd] from-30% to-[#e1ebfa] to-30% block min-h-[2.25rem] sm:min-h-[2.5rem] md:min-h-[3rem] lg:min-h-[3.25rem] xl:min-h-[4rem]">
                  <Typewriter
                    options={{
                      strings: ["Bookkeeping", "CFO Advisory", "USA Company Formation", "Tax Return"],
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 0.1,
                    }}
                  />
                </span>
              </h1>
              <p className="text-[0.95rem] sm:text-base md:text-[1.05rem] lg:text-lg mt-4 text-black leading-relaxed">
                Starting and growing a business in the United States brings new opportunities—but as your business expands, 
                managing tax filing, <Link to="/bookkeeping" className="text-[#0d6efd] font-semibold no-underline hover:text-[#0b5ed7] transition-colors duration-300">bookkeeping</Link> and compliance may start to slow you down. BooksComply helps founders, 
                startups, and small companies keep things simple. From <Link to="/usa-company-formation" className="text-[#0d6efd] font-semibold no-underline hover:text-[#0b5ed7] transition-colors duration-300">U.S. company formation</Link> to daily accounting, 
                we make every step easier so you stay focused on success.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-6">
                <button
                  className="w-full sm:w-auto min-w-[200px] bg-[#0d6efd] text-white font-semibold rounded-full px-6 py-3 shadow-[0_4px_6px_rgba(13,110,253,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(13,110,253,0.3)] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                  onClick={() => setShowContactForm(true)}
                >
                  Book a Free Consultation
                </button>
                <button
                  className="w-full sm:w-auto min-w-[150px] bg-transparent text-[#0d6efd] font-semibold rounded-full px-6 py-3 border-2 border-[#0d6efd] transition-all duration-300 hover:bg-[#0d6efd]/5 hover:-translate-y-0.5"
                  onClick={() => window.location.href = '/pricing'}
                >
                  View Pricing
                </button>
              </div>
            </div>

            {/* Right Column: 3D Animated Feature Hub */}
            <div className="flex items-center justify-center py-4 sm:py-6 md:py-8">
              <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[400px] md:h-[400px] lg:w-[480px] lg:h-[480px] xl:w-[520px] xl:h-[520px]">
                
                {/* Background decorative elements */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#eff6ff]/50 via-transparent to-[#fef3c7]/50"></div>
                
                {/* Outer rotating ring with dots */}
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#0d6efd]/20 animate-[spin_40s_linear_infinite]"></div>
                
                {/* Middle ring */}
                <div className="absolute inset-[12%] rounded-full border border-[#0d6efd]/15"></div>
                
                {/* Inner glow circle */}
                <div className="absolute inset-[22%] rounded-full bg-gradient-to-br from-[#dbeafe] via-white to-[#fef9c3] shadow-[0_0_80px_rgba(13,110,253,0.12)]"></div>
                
                {/* Center BooksComply Logo */}
                <div className="absolute inset-[28%] rounded-full bg-white shadow-[0_12px_40px_rgba(0,0,0,0.1)] flex items-center justify-center z-20 border border-[#e2e8f0] overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafc] to-white"></div>
                  <div className="relative text-center p-3">
                    <div className="text-[1rem] sm:text-[1.25rem] md:text-[1.5rem] lg:text-[1.75rem] xl:text-[2rem] font-extrabold bg-gradient-to-r from-[#1d4ed8] via-[#0d6efd] to-[#dc2626] bg-clip-text text-transparent leading-tight">
                      BooksComply
                    </div>
                    <div className="text-[0.5rem] sm:text-[0.6rem] md:text-[0.7rem] text-[#64748b] mt-0.5 sm:mt-1 font-medium tracking-wide">Your Business Partner</div>
                  </div>
                </div>
                
                {/* Feature Cards - Positioned around center */}
                
                {/* Chat & Support - Top */}
                <div className="absolute top-[2%] left-1/2 -translate-x-1/2 z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(59,130,246,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(59,130,246,0.35)] cursor-pointer border border-[#bfdbfe]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#2563eb]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                        <path d="M7 9h10v2H7zm0-3h10v2H7z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#1e40af] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#dbeafe] whitespace-nowrap">Chat & Support</span>
                  </div>
                </div>
                
                {/* Personalized Dashboard - Top Right */}
                <div className="absolute top-[18%] right-[3%] sm:right-[5%] z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(139,92,246,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(139,92,246,0.35)] cursor-pointer border border-[#ddd6fe]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#7c3aed]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#6d28d9] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#ede9fe]">Dashboard</span>
                  </div>
                </div>
                
                {/* Accounts & Billing - Right */}
                <div className="absolute top-[50%] right-[-2%] sm:right-[0%] -translate-y-1/2 z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(16,185,129,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(16,185,129,0.35)] cursor-pointer border border-[#a7f3d0]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#059669]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#047857] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#d1fae5]">Billing</span>
                  </div>
                </div>
                
                {/* Documents Repository - Bottom Right */}
                <div className="absolute bottom-[18%] right-[3%] sm:right-[5%] z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(245,158,11,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(245,158,11,0.35)] cursor-pointer border border-[#fde68a]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#d97706]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#b45309] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#fef3c7]">Documents</span>
                  </div>
                </div>
                
                {/* Status Tracking - Bottom */}
                <div className="absolute bottom-[2%] left-1/2 -translate-x-1/2 z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(239,68,68,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(239,68,68,0.35)] cursor-pointer border border-[#fecaca]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#dc2626]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#b91c1c] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#fee2e2] whitespace-nowrap">Status Tracking</span>
                  </div>
                </div>
                
                {/* Documents Left Bottom */}
                <div className="absolute bottom-[18%] left-[3%] sm:left-[5%] z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(236,72,153,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(236,72,153,0.35)] cursor-pointer border border-[#fbcfe8]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#db2777]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01l1.41 1.41L11 14.84V19h2v-4.16l1.59 1.59L16 15.01 12.01 11z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#be185d] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#fce7f3]">Repository</span>
                  </div>
                </div>
                
                {/* Anytime Access - Left */}
                <div className="absolute top-[50%] left-[-2%] sm:left-[0%] -translate-y-1/2 z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(6,182,212,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(6,182,212,0.35)] cursor-pointer border border-[#a5f3fc]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#0891b2]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#0e7490] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#cffafe]">24/7 Access</span>
                  </div>
                </div>
                
                {/* Top Left */}
                <div className="absolute top-[18%] left-[3%] sm:left-[5%] z-10">
                  <div className="group flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl bg-white shadow-[0_4px_20px_rgba(99,102,241,0.25)] flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(99,102,241,0.35)] cursor-pointer border border-[#c7d2fe]">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-[#4f46e5]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                      </svg>
                    </div>
                    <span className="mt-1 sm:mt-2 text-[0.55rem] sm:text-[0.65rem] md:text-xs font-semibold text-[#4338ca] bg-white/90 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm border border-[#e0e7ff]">Secure</span>
                  </div>
                </div>
                
                {/* Floating particles effect */}
                <div className="absolute top-[30%] left-[25%] w-2 h-2 rounded-full bg-[#3b82f6]/30 animate-ping"></div>
                <div className="absolute top-[60%] right-[20%] w-1.5 h-1.5 rounded-full bg-[#8b5cf6]/30 animate-ping" style={{ animationDelay: '1s' }}></div>
                <div className="absolute bottom-[35%] left-[30%] w-1.5 h-1.5 rounded-full bg-[#10b981]/30 animate-ping" style={{ animationDelay: '2s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lazy loaded sections with min-height fallback to prevent CLS */}
      <Suspense fallback={
        <div style={{ minHeight: '2000px' }} className="animate-pulse">
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-6"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-100 rounded-2xl"></div>)}
              </div>
            </div>
          </div>
        </div>
      }>
        <ServiceCard />
        <WhatWeDoSection />
        <GoogleReviewSection />
        <Section4 />
        <LogoSection />
        <HomeFAQs />
      </Suspense>

      {/* Contact Form Modal */}
      {showContactForm && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="text-white">Loading...</div></div>}>
          <ContactForm onClose={handleCloseContactForm} />
        </Suspense>
      )}
    </main>
  );
};
export default Home;